import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../AppLayout';
import axios from 'axios';

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const soundWave = keyframes`
  0%, 100% { height: 10%; }
  50% { height: 100%; }
`;

const PageContainer = styled.div`
  padding: 40px 20px 80px;
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const PageTitle = styled(motion.h1)`
  font-family: 'Orbitron', sans-serif;
  font-size: 36px;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 15px;
`;

const PageDesc = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled(motion.div)`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 30px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, var(--primary), var(--secondary), var(--accent));
  }
`;

const PanelTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 18px;
  color: var(--primary);
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Select = styled.select`
  width: 100%;
  padding: 14px 16px;
  background: var(--bg-dark);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 15px rgba(0, 240, 255, 0.3);
    outline: none;
  }
  
  option {
    background: var(--bg-dark);
    color: var(--text-primary);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 14px 16px;
  background: var(--bg-dark);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 15px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 15px rgba(0, 240, 255, 0.3);
    outline: none;
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const SliderGroup = styled.div`
  margin-bottom: 20px;
`;

const SliderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const SliderValue = styled.span`
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  color: var(--primary);
`;

const Slider = styled.input`
  width: 100%;
  height: 6px;
  -webkit-appearance: none;
  background: var(--bg-dark);
  border-radius: 3px;
  outline: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    background: linear-gradient(135deg, var(--primary), var(--secondary));
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 0 10px rgba(0, 240, 255, 0.5);
  }
`;

const ActionBtn = styled(motion.button)`
  width: 100%;
  padding: 16px;
  background: ${props => props.$primary ? 
    'linear-gradient(135deg, var(--primary), var(--secondary))' : 
    'transparent'};
  border: ${props => props.$primary ? 'none' : '2px solid var(--primary)'};
  border-radius: 12px;
  color: #fff;
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const VoiceList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 15px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 10px;
`;

const VoiceCard = styled(motion.div)`
  background: var(--bg-dark);
  border: 1px solid ${props => props.$selected ? 'var(--primary)' : 'var(--border-color)'};
  border-radius: 12px;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--primary);
    transform: translateY(-3px);
  }
`;

const VoiceName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 5px;
`;

const VoiceSex = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const Visualizer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;
  height: 100px;
  margin: 20px 0;
`;

const Bar = styled.div`
  width: 6px;
  background: linear-gradient(180deg, var(--primary), var(--accent));
  border-radius: 3px;
  animation: ${soundWave} ${props => 0.5 + Math.random() * 0.5}s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
`;

const ResultPanel = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const ResultAudio = styled.audio`
  width: 100%;
  margin-top: 20px;
  
  &::-webkit-media-controls-panel {
    background: var(--bg-dark);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  background: ${props => {
    switch(props.$status) {
      case 'success': return 'rgba(0, 255, 136, 0.2)';
      case 'processing': return 'rgba(255, 170, 0, 0.2)';
      case 'error': return 'rgba(255, 51, 102, 0.2)';
      default: return 'rgba(160, 160, 176, 0.2)';
    }
  }};
  color: ${props => {
    switch(props.$status) {
      case 'success': return 'var(--success)';
      case 'processing': return 'var(--warning)';
      case 'error': return 'var(--danger)';
      default: return 'var(--text-secondary)';
    }
  }};
`;

const languages = [
  { code: 'zh-CN', name: '中文（普通话）' },
  { code: 'zh-HK', name: '中文（粤语）' },
  { code: 'en-US', name: '英语（美国）' },
  { code: 'en-GB', name: '英语（英国）' },
  { code: 'ja-JP', name: '日语' },
  { code: 'ko-KR', name: '韩语' },
  { code: 'fr-FR', name: '法语' },
  { code: 'de-DE', name: '德语' },
];

const VoicePage = () => {
  const [publicVoices, setPublicVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [content, setContent] = useState('');
  const [language, setLanguage] = useState('zh-CN');
  const [speed, setSpeed] = useState(1.0);
  const [pitch, setPitch] = useState(1.0);
  const [volume, setVolume] = useState(1.0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState('idle');
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchPublicVoices();
  }, []);

  const fetchPublicVoices = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v1/voice/list', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('公模声音响应:', response.data);
      
      if (response.data.code === 1) {
        const dataList = response.data.data;
        if (dataList && Array.isArray(dataList) && dataList.length > 0) {
          const voices = dataList.map((v, index) => ({
            id: v.voice_id || v.id || index + 1,
            name: v.voice_name || v.name || `声音${index + 1}`,
            sex: v.sex === 1 ? '男' : '女',
            clone_status: v.clone_status
          }));
          setPublicVoices(voices);
          if (voices.length > 0) {
            setSelectedVoice(voices[0]);
          }
        } else {
          console.warn('公模声音列表为空');
        }
      }
    } catch (error) {
      console.error('获取声音列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v1/voice/history', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.code === 1 && response.data.data) {
        setHistory(response.data.data);
      }
    } catch (error) {
      console.error('获取历史记录失败:', error);
    }
  };

  const handleViewHistory = () => {
    fetchHistory();
    setShowHistory(true);
  };

  const handleDownload = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleGenerate = async () => {
    if (!content.trim()) {
      alert('请输入要合成的内容');
      return;
    }

    if (!selectedVoice) {
      alert('请选择音色');
      return;
    }

    setIsGenerating(true);
    setStatus('processing');
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/v1/voice/merge', {
        voice_id: selectedVoice.id,
        content: content,
        model_type: '1',
        language: language,
        speed_ratio: speed,
        pitch_ratio: pitch,
        volume_ratio: volume
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('合成响应:', response.data);
      console.log('合成响应data:', JSON.stringify(response.data.data));
      
      if (response.data.code === 1) {
        const data = response.data.data;
        
        // 检查是否直接返回了音频URL
        const audioUrl = data.audio_oss_url || data.audio_url || data.url || data.file_url;
        
        if (audioUrl) {
          setResult({ ...data, audio_url: audioUrl });
          setStatus('success');
          // 重新获取历史记录
          fetchHistory();
        } else if (data.audio_id || data.id || data.task_id) {
          const audioId = data.audio_id || data.id || data.task_id;
          setResult({ audio_id: audioId, status: 'processing' });
          setStatus('processing');
          
          let pollCount = 0;
          const maxPolls = 30;
          
          const pollStatus = async () => {
            pollCount++;
            try {
              const statusRes = await axios.get(
                `/api/v1/voice/merge/status/${audioId}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
              );
              console.log('状态响应:', statusRes.data);
              
              if (statusRes.data.code === 1) {
                const statusData = statusRes.data.data;
                const statusUrl = statusData.audio_oss_url || statusData.audio_url || statusData.url || statusData.file_url;
                
                // 根据API文档，merge_status为1表示处理中
                if (statusUrl) {
                  setResult({ ...statusData, audio_url: statusUrl });
                  setStatus('success');
                  // 重新获取历史记录
                  fetchHistory();
                } else if (pollCount < maxPolls && (statusData.merge_status === 1 || statusData.status === 1 || statusData.status === 'processing')) {
                  setTimeout(pollStatus, 3000); // 增加轮询间隔
                } else if (pollCount >= maxPolls) {
                  setResult({ ...statusData, audio_url: null, error: '合成超时' });
                  setStatus('error');
                } else {
                  setResult({ ...statusData, audio_url: null });
                  setStatus('error');
                }
              } else if (pollCount < maxPolls) {
                setTimeout(pollStatus, 3000);
              } else {
                setResult({ error: '合成失败' });
                setStatus('error');
              }
            } catch (err) {
              console.error('查询状态失败:', err);
              if (pollCount < maxPolls) {
                setTimeout(pollStatus, 3000);
              } else {
                setResult({ error: '查询状态失败' });
                setStatus('error');
              }
            }
          };
          
          pollStatus();
        } else {
          setResult(data);
          setStatus('success');
        }
      } else {
        alert(response.data.msg || '合成失败');
        setStatus('error');
      }
    } catch (error) {
      console.error('API调用失败:', error);
      alert(error.response?.data?.detail || '合成失败，请检查网络连接');
      setStatus('error');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Layout>
      <PageContainer>
        <PageHeader>
          <PageTitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            声音合成
          </PageTitle>
          <PageDesc>输入文本，选择音色，即可生成自然流畅的语音</PageDesc>
        </PageHeader>

        <MainGrid>
          <Panel
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <PanelTitle>
              <span>🎤</span> 选择音色
            </PanelTitle>
            
            <VoiceList>
              {publicVoices.map((voice) => (
                <VoiceCard
                  key={voice.id}
                  $selected={selectedVoice.id === voice.id}
                  onClick={() => setSelectedVoice(voice)}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <VoiceName>{voice.name}</VoiceName>
                  <VoiceSex>{voice.sex}</VoiceSex>
                </VoiceCard>
              ))}
            </VoiceList>
          </Panel>

          <Panel
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <PanelTitle>
              <span>⚙️</span> 合成参数
            </PanelTitle>
            
            <FormGroup>
              <Label>语言</Label>
              <Select value={language} onChange={(e) => setLanguage(e.target.value)}>
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </Select>
            </FormGroup>

            <SliderGroup>
              <SliderHeader>
                <Label>语速</Label>
                <SliderValue>{speed.toFixed(1)}x</SliderValue>
              </SliderHeader>
              <Slider 
                type="range" 
                min="0.5" 
                max="2.0" 
                step="0.1" 
                value={speed}
                onChange={(e) => setSpeed(parseFloat(e.target.value))}
              />
            </SliderGroup>

            <SliderGroup>
              <SliderHeader>
                <Label>音调</Label>
                <SliderValue>{pitch.toFixed(1)}</SliderValue>
              </SliderHeader>
              <Slider 
                type="range" 
                min="0.5" 
                max="2.0" 
                step="0.1" 
                value={pitch}
                onChange={(e) => setPitch(parseFloat(e.target.value))}
              />
            </SliderGroup>

            <SliderGroup>
              <SliderHeader>
                <Label>音量</Label>
                <SliderValue>{Math.round(volume * 100)}%</SliderValue>
              </SliderHeader>
              <Slider 
                type="range" 
                min="0.1" 
                max="2.0" 
                step="0.1" 
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
              />
            </SliderGroup>
          </Panel>
        </MainGrid>

        <Panel 
          style={{ marginTop: '40px' }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <PanelTitle>
            <span>📝</span> 输入文本
          </PanelTitle>
          
          <TextArea 
            placeholder="请输入您想要合成的文本内容...&#10;支持中英文混合输入，最多支持5000字符。"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={5000}
          />
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginTop: '15px' 
          }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
              {content.length} / 5000 字符
            </span>
            
            <ActionBtn 
              onClick={handleGenerate}
              disabled={isGenerating || !content.trim()}
              $primary
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isGenerating ? '合成中...' : '开始合成'}
            </ActionBtn>
            <ActionBtn 
              onClick={handleViewHistory}
              disabled={isGenerating}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{ marginLeft: '10px' }}
            >
              历史记录
            </ActionBtn>
          </div>
        </Panel>

        <AnimatePresence>
          {status !== 'idle' && (
            <ResultPanel
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Panel style={{ marginTop: '40px', padding: '40px' }}>
                <StatusBadge $status={status}>
                  {status === 'processing' && '合成中'}
                  {status === 'success' && '合成成功'}
                  {status === 'error' && '合成失败'}
                </StatusBadge>
                
                {status === 'processing' && (
                  <Visualizer>
                    {[...Array(20)].map((_, i) => (
                      <Bar key={i} $delay={i * 0.1} />
                    ))}
                  </Visualizer>
                )}
                
                {status === 'success' && result && (
                  <div style={{ marginTop: '20px' }}>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '15px' }}>
                      音频已生成完毕
                    </p>
                    {console.log('音频结果所有字段:', result)}
                    <ResultAudio 
                      controls 
                      src={result.audio_url || result.audio_oss_url || result.url || result.oss_url || result.file_url || ''}
                    />
                    {(result.audio_url || result.audio_oss_url || result.url || result.oss_url || result.file_url) && (
                      <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
                        <button 
                          onClick={() => handleDownload(result.audio_url || result.audio_oss_url || result.url || result.oss_url || result.file_url)}
                          style={{
                            background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          下载音频
                        </button>
                      </div>
                    )}
                    {result.audio_url && (
                      <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                        音频链接: {result.audio_url}
                      </p>
                    )}
                  </div>
                )}
                
                {status === 'error' && result && result.error && (
                  <div style={{ marginTop: '20px', color: 'var(--danger)' }}>
                    <p>{result.error}</p>
                    <button 
                      onClick={() => setStatus('idle')}
                      style={{
                        background: 'transparent',
                        border: '1px solid var(--primary)',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        color: 'var(--primary)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        marginTop: '10px'
                      }}
                    >
                      重新开始
                    </button>
                  </div>
                )}
              </Panel>
            </ResultPanel>
          )}
        </AnimatePresence>
        
        <HistoryModal 
          isOpen={showHistory} 
          onClose={() => setShowHistory(false)} 
          history={history}
          onDownload={handleDownload}
        />
      </PageContainer>
    </Layout>
  );
};

export default VoicePage;

const HistoryModal = ({ isOpen, onClose, history, onDownload }) => {
  if (!isOpen) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        backgroundColor: '#1a1a2e',
        borderRadius: '16px',
        padding: '30px',
        maxWidth: '800px',
        maxHeight: '80vh',
        overflow: 'auto',
        border: '1px solid #16213e'
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: '#fff', margin: 0 }}>音频合成历史记录</h2>
          <button onClick={onClose} style={{
            background: 'none',
            border: 'none',
            color: '#fff',
            fontSize: '24px',
            cursor: 'pointer'
          }}>×</button>
        </div>
        
        {history.length === 0 ? (
          <p style={{ color: '#888', textAlign: 'center' }}>暂无历史记录</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {history.map((item, index) => (
              <div key={index} style={{
                background: '#16213e',
                borderRadius: '12px',
                padding: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ flex: 1 }}>
                  <p style={{ color: '#fff', margin: '0 0 8px 0', fontSize: '14px' }}>
                    {item.text_content?.substring(0, 50)}{item.text_content?.length > 50 ? '...' : ''}
                  </p>
                  <p style={{ color: '#888', margin: 0, fontSize: '12px' }}>
                    音色ID: {item.voice_id} | 状态: {item.status === 1 ? '成功' : '处理中'} | 
                    时间: {new Date(item.created_at * 1000).toLocaleString()}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {item.audio_url && (
                    <>
                      <audio controls src={item.audio_url} style={{ height: '32px' }} />
                      <button onClick={() => onDownload(item.audio_url)} style={{
                        background: '#4ade80',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        color: '#000',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}>下载</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
